import React, { useState, useRef, useCallback } from 'react';
import Icon from '../../../components/AppIcon';

import Input from '../../../components/ui/Input';

const ImageHotspotEditor = ({ gameConfig, setGameConfig }) => {
  const [selectedHotspot, setSelectedHotspot] = useState(null);
  const [previewMode, setPreviewMode] = useState(false);
  const imageRef = useRef(null);
  const fileInputRef = useRef(null);

  const hotspots = gameConfig?.hotspots || [];
  const imageUrl = gameConfig?.hotspotImage || '';

  const handleImageUpload = (e) => {
    const file = e?.target?.files?.[0];
    if (!file) return;
    if (file?.size > 5 * 1024 * 1024) {
      alert('Image must be less than 5MB');
      return;
    }
    const reader = new FileReader();
    reader.onloadend = () => {
      setGameConfig(prev => ({
        ...prev,
        hotspotImage: reader?.result,
        hotspots: prev?.hotspots || []
      }));
      setSelectedHotspot(null);
    };
    reader?.readAsDataURL(file);
  };

  const handleImageClick = useCallback((e) => {
    if (previewMode || !imageUrl) return;
    const rect = imageRef?.current?.getBoundingClientRect();
    if (!rect) return;
    const x = ((e?.clientX - rect?.left) / rect?.width) * 100;
    const y = ((e?.clientY - rect?.top) / rect?.height) * 100;
    const newHotspot = {
      id: Date.now(),
      x: parseFloat(x?.toFixed(2)),
      y: parseFloat(y?.toFixed(2)),
      question: '',
      options: ['', '', '', ''],
      correctAnswer: ''
    };
    const newHotspots = [...hotspots, newHotspot];
    setGameConfig(prev => ({ ...prev, hotspots: newHotspots }));
    setSelectedHotspot(newHotspot?.id);
  }, [previewMode, imageUrl, hotspots, setGameConfig]);

  const handleDeleteHotspot = (id, e) => {
    e?.stopPropagation();
    const updated = hotspots?.filter(h => h?.id !== id);
    setGameConfig(prev => ({ ...prev, hotspots: updated }));
    if (selectedHotspot === id) setSelectedHotspot(null);
  };

  const updateHotspot = (id, field, value) => {
    const updated = hotspots?.map(h => h?.id === id ? { ...h, [field]: value } : h);
    setGameConfig(prev => ({ ...prev, hotspots: updated }));
  };

  const updateHotspotOption = (id, optIndex, value) => {
    const updated = hotspots?.map(h => {
      if (h?.id !== id) return h;
      const newOptions = [...(h?.options || ['', '', '', ''])];
      newOptions[optIndex] = value;
      return { ...h, options: newOptions };
    });
    setGameConfig(prev => ({ ...prev, hotspots: updated }));
  };

  const activeHotspot = hotspots?.find(h => h?.id === selectedHotspot);

  return (
    <div className="space-y-4">
      {/* Image Upload */}
      {!imageUrl ? (
        <div>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleImageUpload}
            className="hidden"
            id="hotspot-image-upload"
          />
          <label
            htmlFor="hotspot-image-upload"
            className="flex flex-col items-center justify-center w-full h-56 border-2 border-dashed border-border rounded-lg cursor-pointer hover:border-primary transition-organic bg-muted/30"
          >
            <Icon name="ImagePlus" size={40} className="text-muted-foreground mb-3" />
            <span className="text-base text-muted-foreground mb-1">Upload Hotspot Image</span>
            <span className="text-sm text-muted-foreground">PNG, JPG, WEBP (max 5MB)</span>
          </label>
        </div>
      ) : (
        <div className="space-y-3">
          {/* Toolbar */}
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setPreviewMode(false)}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-organic ${
                  !previewMode ? 'bg-primary text-white' : 'bg-muted text-muted-foreground hover:bg-muted/80'
                }`}
              >
                <span className="flex items-center gap-1.5">
                  <Icon name="MapPin" size={14} />
                  Edit
                </span>
              </button>
              <button
                onClick={() => { setPreviewMode(true); setSelectedHotspot(null); }}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-organic ${
                  previewMode ? 'bg-primary text-white' : 'bg-muted text-muted-foreground hover:bg-muted/80'
                }`}
              >
                <span className="flex items-center gap-1.5">
                  <Icon name="Eye" size={14} />
                  Preview
                </span>
              </button>
            </div>
            <div className="flex items-center space-x-2">
              <span className="text-xs text-muted-foreground">
                {hotspots?.length} hotspot{hotspots?.length !== 1 ? 's' : ''}
              </span>
              <button
                onClick={() => fileInputRef?.current?.click()}
                className="text-xs text-primary hover:underline"
              >
                Change image
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleImageUpload}
                className="hidden"
              />
            </div>
          </div>

          {/* Image with hotspot markers */}
          <div className="relative rounded-lg overflow-hidden border border-border bg-muted/20">
            <img
              ref={imageRef}
              src={imageUrl}
              alt="Hotspot game image - click to place markers"
              className={`w-full object-contain max-h-80 ${
                !previewMode ? 'cursor-crosshair' : 'cursor-default'
              }`}
              onClick={handleImageClick}
              draggable={false}
            />
            {/* Hotspot markers */}
            {hotspots?.map((hotspot, index) => (
              <div
                key={hotspot?.id}
                className="absolute"
                style={{
                  left: `${hotspot?.x}%`,
                  top: `${hotspot?.y}%`,
                  transform: 'translate(-50%, -50%)'
                }}
                onClick={(e) => {
                  e?.stopPropagation();
                  if (!previewMode) setSelectedHotspot(hotspot?.id);
                }}
              >
                <div
                  className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold shadow-lg border-2 cursor-pointer transition-all ${
                    selectedHotspot === hotspot?.id
                      ? 'bg-yellow-400 border-yellow-600 text-yellow-900 scale-125' :'bg-primary border-white text-white hover:scale-110'
                  }`}
                >
                  {index + 1}
                </div>
                {!previewMode && (
                  <button
                    onClick={(e) => handleDeleteHotspot(hotspot?.id, e)}
                    className="absolute -top-2 -right-2 w-4 h-4 bg-destructive rounded-full flex items-center justify-center opacity-0 hover:opacity-100 group-hover:opacity-100 transition-opacity"
                    title="Delete hotspot"
                  >
                    <Icon name="X" size={10} className="text-white" />
                  </button>
                )}
              </div>
            ))}
            {!previewMode && (
              <div className="absolute bottom-2 left-2 bg-black/60 text-white text-xs px-2 py-1 rounded">
                Click on image to add hotspot
              </div>
            )}
          </div>

          {/* Hotspot list */}
          {hotspots?.length > 0 && !previewMode && (
            <div className="space-y-2">
              <label className="text-sm font-medium text-foreground">Hotspots</label>
              <div className="space-y-1 max-h-40 overflow-y-auto">
                {hotspots?.map((hotspot, index) => (
                  <div
                    key={hotspot?.id}
                    onClick={() => setSelectedHotspot(hotspot?.id)}
                    className={`flex items-center justify-between p-2 rounded-lg cursor-pointer transition-organic border ${
                      selectedHotspot === hotspot?.id
                        ? 'border-primary bg-primary/5' :'border-border hover:border-primary/50'
                    }`}
                  >
                    <div className="flex items-center space-x-2">
                      <div className="w-6 h-6 rounded-full bg-primary flex items-center justify-center text-xs font-bold text-white flex-shrink-0">
                        {index + 1}
                      </div>
                      <span className="text-sm text-foreground truncate">
                        {hotspot?.question?.trim() || <span className="text-muted-foreground italic">No question yet</span>}
                      </span>
                    </div>
                    <button
                      onClick={(e) => handleDeleteHotspot(hotspot?.id, e)}
                      className="text-destructive hover:text-destructive/80 ml-2 flex-shrink-0"
                    >
                      <Icon name="Trash2" size={14} />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Selected hotspot editor */}
          {activeHotspot && !previewMode && (
            <div className="p-4 bg-muted/30 rounded-lg border border-primary/20 space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm font-semibold text-foreground">
                  Hotspot #{hotspots?.findIndex(h => h?.id === activeHotspot?.id) + 1} — Question
                </span>
                <button
                  onClick={() => setSelectedHotspot(null)}
                  className="text-muted-foreground hover:text-foreground"
                >
                  <Icon name="X" size={16} />
                </button>
              </div>
              <Input
                type="text"
                placeholder="Enter question for this hotspot"
                value={activeHotspot?.question || ''}
                onChange={(e) => updateHotspot(activeHotspot?.id, 'question', e?.target?.value)}
              />
              <div className="space-y-2">
                <label className="text-xs font-medium text-muted-foreground">Answer Options</label>
                {(activeHotspot?.options || ['', '', '', ''])?.map((opt, i) => (
                  <div key={i} className="flex items-center space-x-2">
                    <span className="text-xs text-muted-foreground w-4 flex-shrink-0">{String.fromCharCode(65 + i)}.</span>
                    <input
                      type="text"
                      placeholder={`Option ${String.fromCharCode(65 + i)}`}
                      value={opt}
                      onChange={(e) => updateHotspotOption(activeHotspot?.id, i, e?.target?.value)}
                      className="flex-1 px-3 py-1.5 bg-card border border-border rounded-md text-sm text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring transition-organic"
                    />
                  </div>
                ))}
              </div>
              <div>
                <label className="text-xs font-medium text-muted-foreground block mb-1">Correct Answer</label>
                <select
                  value={activeHotspot?.correctAnswer || ''}
                  onChange={(e) => updateHotspot(activeHotspot?.id, 'correctAnswer', e?.target?.value)}
                  className="w-full px-3 py-1.5 bg-card border border-border rounded-md text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring transition-organic"
                >
                  <option value="">Select correct answer</option>
                  {(activeHotspot?.options || [])?.map((opt, i) => (
                    opt?.trim() && (
                      <option key={i} value={String(i)}>
                        {String.fromCharCode(65 + i)}. {opt}
                      </option>
                    )
                  ))}
                </select>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ImageHotspotEditor;
